import {
  List,
  TextField,
  EditButton,
  ShowButton,
  ReferenceField,
  Datagrid,
  ReferenceArrayField,
  SingleFieldList,
  ChipField,
  SimpleList,
  ReferenceInput,
  SelectInput,
  TopToolbar,
  CreateButton,
  FilterButton,
  AutocompleteInput,
  TextInput,
} from "react-admin";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { useMediaQuery } from "@mui/material";
import { EmptyList } from "server/components";
import { settings } from "app";
import { ExportXlsButton } from "features/ExportXlsxButton";
import { InternalRoomsExporter } from "./XlsxExporter";
import { administrationRequests } from "api/administrationApi";
import { MobileListRow } from "features/MobileListRow";

const RoomFilters = [
  <TextInput
    source="name"
    label="Име"
    alwaysOn
    style={{ width: 300 }}
  />,
  <ReferenceInput
    source="buildingId"
    reference={administrationRequests.getBuildingsSelect()}
    label="Сграда"
    alwaysOn
  >
    <AutocompleteInput
      label="Сграда"
      optionText="name"
      optionValue="id"
      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
      style={{ width: 300 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="typeId"
    reference={administrationRequests.getRoomTypesSelect()}
    label="Тип зала"
    alwaysOn={false}
  >
    <SelectInput
      label="Тип зала"
      optionText="name"
      emptyText="Всички"
      style={{ width: 300 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="classTypeId"
    reference={administrationRequests.getRoomClassTypesSelect()}
    label="Тип занятие"
    alwaysOn={false}
  >
    <SelectInput
      label="Тип занятие"
      optionText="name"
      emptyText="Всички"
      style={{ width: 300 }}
    />
  </ReferenceInput>,
  <ReferenceInput
    source="departmentId"
    reference={administrationRequests.getDepartmentsSelect()}
    label="Катедра"
    alwaysOn
  >
    <SelectInput
      label="Катедра"
      optionText="fullName"
      emptyText="Всички"
      style={{ width: 300 }}
    />
  </ReferenceInput>,
];

const ListRooms = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const userPermissions = getPermissions();

  const ListActions = hasCreate => {
    return (
      <TopToolbar>
        <FilterButton />
        {hasCreate && <CreateButton />}
        <ExportXlsButton exporter={InternalRoomsExporter} />
      </TopToolbar>
    );
  };

  return (
    <List
      title="Зали"
      actions={<ListActions hasCreate={checkPermission(userPermissions, permissions.canCreateRooms)} />}
      filters={RoomFilters}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => <MobileListRow fields={[{ label: "Име", value: record.nameBg }]} />}
          secondaryText={record => (
            <div>
              <MobileListRow
                fields={[
                  {
                    label: "Сграда",
                    value: (
                      <ReferenceField
                        source="buildingId"
                        reference={administrationRequests.getBuildingsSelect()}
                      >
                        <TextField
                          source="name"
                          label="Сграда"
                        />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Етаж", value: record.floor }]} />
              <MobileListRow fields={[{ label: "Номер", value: record.number }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Тип",
                    value: (
                      <ReferenceField
                        source="typeId"
                        reference={administrationRequests.getRoomTypesSelect()}
                      >
                        <TextField
                          source="name"
                          label="Тип"
                        />
                      </ReferenceField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  { label: "Капацитет за лекции/упражнения", value: record.capacityClasses },
                ]}
              />
              <MobileListRow fields={[{ label: "Капацитет за изпит", value: record.capacityExam }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Тип занятия",
                    value: (
                      <ReferenceArrayField
                        source="classTypeIds"
                        reference={administrationRequests.getRoomClassTypesSelect()}
                      >
                        <SingleFieldList linkType={false}>
                          <ChipField source="name" />
                        </SingleFieldList>
                      </ReferenceArrayField>
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Катедра",
                    value: (
                      <ReferenceArrayField
                        source="departmentIds"
                        reference={administrationRequests.getDepartmentsSelect()}
                        emptyText="-"
                      >
                        <SingleFieldList linkType={false}>
                          <ChipField
                            source="name"
                            emptyText="-"
                          />
                        </SingleFieldList>
                      </ReferenceArrayField>
                    ),
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={() => (
            <>
              {checkPermission(getPermissions(), permissions.canUpdateRooms) && <EditButton label="" />}
              <ShowButton label="" />
            </>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
        >
          <TextField
            source="nameBg"
            label={<span style={{ display: "block", textAlign: "center" }}>Име</span>}
            sortable={false}
          />

          <ReferenceField
            label="Сграда"
            source="buildingId"
            reference={administrationRequests.getBuildingsSelect()}
          >
            <TextField
              source="name"
              label="Сграда"
            />
          </ReferenceField>
          <TextField
            source="floor"
            label="Етаж"
            textAlign="center"
            sortable={false}
          />
          <TextField
            source="number"
            label="Номер"
            textAlign="center"
            sortable={false}
          />
          <ReferenceField
            label="Тип"
            source="typeId"
            reference={administrationRequests.getRoomTypesSelect()}
          >
            <TextField
              textAlign="center"
              source="name"
              label="Тип"
            />
          </ReferenceField>

          <TextField
            textAlign="center"
            source="capacityClasses"
            label={
              <span>
                Капацитет за
                <br />
                лекции/упражнения
              </span>
            }
            sortable={false}
          />
          <TextField
            source="capacityExam"
            textAlign="center"
            label="Капацитет за изпит"
            sortable={false}
          />

          <ReferenceArrayField
            label="Тип занятия"
            source="classTypeIds"
            reference={administrationRequests.getRoomClassTypesSelect()}
          >
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ReferenceArrayField>
          <ReferenceArrayField
            label="Катедра"
            source="departmentIds"
            reference={administrationRequests.getDepartmentsSelect()}
            emptyText="-"
          >
            <SingleFieldList linkType={false}>
              <ChipField
                source="name"
                emptyText="-"
              />
            </SingleFieldList>
          </ReferenceArrayField>

          {checkPermission(getPermissions(), permissions.canUpdateRooms) && <EditButton />}
          <ShowButton />
        </Datagrid>
      )}
    </List>
  );
};
export default ListRooms;
